const EPSILON = 0.000001;

const numeric = (value, fallback = 0) => (
  Number.isFinite(Number(value)) ? Number(value) : fallback
);

const bounds = (clip) => {
  const start = Math.max(0, numeric(clip?.startBeat));
  const duration = Math.max(0, numeric(clip?.durationBeats));
  return { start, duration, end: start + duration };
};

/** Resolve a clip's visible placement against the other items on its lane. */
export function resolveLaneClipPlacement(clips = [], clipId, requestedStart, requestedDuration) {
  const startRequested = Math.max(0, numeric(requestedStart));
  const durationRequested = Math.max(0, numeric(requestedDuration));
  const neighbors = (Array.isArray(clips) ? clips : [])
    .filter((clip) => clip?.id !== clipId)
    .map((clip) => ({ clip, ...bounds(clip) }))
    .sort((a, b) => a.start - b.start || a.end - b.end);

  // A move that lands inside an earlier clip continues at that clip's end.
  // Starting before a later clip is allowed; the duration is trimmed to that
  // later clip's exact boundary below.
  const containing = neighbors.filter((neighbor) => (
    neighbor.start < startRequested - EPSILON && neighbor.end > startRequested + EPSILON
  ));
  const startBeat = containing.length
    ? Math.max(startRequested, ...containing.map((neighbor) => neighbor.end))
    : startRequested;
  const next = neighbors.find((neighbor) => neighbor.start >= startBeat - EPSILON);
  const maxDuration = next ? Math.max(0, next.start - startBeat) : durationRequested;

  return { startBeat, durationBeats: Math.min(durationRequested, maxDuration) };
}

/** Normalize a whole timeline so no two visible clips on a lane overlap. */
export function normalizeLaneClipOverlaps(clips = []) {
  if (!Array.isArray(clips) || clips.length < 2) return clips;
  const byLane = new Map();
  clips.forEach((clip, index) => {
    const lane = String(clip?.trackId || '__unassigned__');
    if (!byLane.has(lane)) byLane.set(lane, []);
    byLane.get(lane).push({ clip, index });
  });
  const normalized = [...clips];
  byLane.forEach((entries) => {
    entries.sort((a, b) => {
      const startDelta = bounds(a.clip).start - bounds(b.clip).start;
      return startDelta || a.index - b.index;
    });
    entries.forEach((entry, index) => {
      const current = bounds(entry.clip);
      const next = entries[index + 1];
      if (!next) return;
      const nextStart = bounds(next.clip).start;
      const visibleDuration = Math.max(0, Math.min(current.duration, nextStart - current.start));
      if (visibleDuration < current.duration - EPSILON) {
        normalized[entry.index] = { ...entry.clip, durationBeats: visibleDuration };
      }
    });
  });
  return normalized;
}

export default resolveLaneClipPlacement;
